import { ContentCard } from "@msuite/ui/content-card";
import { DataGrid } from "@msuite/ui/data-grid";
import { icon } from "@msuite/ui/icons";
import { Link } from "@msuite/ui/link";
import { useTranslation } from "@msuite/ui/localization";
import type { IncomingInvoiceDetail, Nullable } from "@msuite/utils/types";
import { formatInvoiceAmount, formatInvoiceDate } from "@/components/incoming-invoices";
import { useHiddenProjectTabs } from "@/hooks/use-project-tabs";
import { projectTabPath } from "@/sites/projects/project/tabs";
import { IncomingInvoicePaymentActions } from "./incoming-invoice-payment-actions";

/** Props Interface */
interface IncomingInvoicePaymentProps {
	incomingInvoice: Nullable<IncomingInvoiceDetail>;
}

/**
 * The transfer that paid the invoice, while it is paid; nothing otherwise. The outgoing invoices
 * offset against it are listed beneath, each linked to its project's invoices, and a payment they
 * covered in full says it went out without a transfer.
 */
export function IncomingInvoicePayment({ incomingInvoice }: IncomingInvoicePaymentProps) {
	/** Context */
	const { t } = useTranslation();

	/** Hooks */
	const hiddenTabs = useHiddenProjectTabs();

	/** Computed */
	const payment = incomingInvoice?.payment;
	const hasOffsets = (payment?.offsets.length ?? 0) > 0;

	/** Guard */
	if (!incomingInvoice || !payment) return null;

	/** Render */
	return (
		<ContentCard
			title={t("incoming_invoices.payment")}
			icon={icon("Landmark")}
			accessory={
				<IncomingInvoicePaymentActions
					incomingInvoiceId={incomingInvoice.id}
					paymentRunId={payment.paymentRunId}
					hasTransfer={payment.amount > 0}
				/>
			}
		>
			<DataGrid>
				{payment.offsets.map((offset) => (
					<DataGrid.Row key={offset.outgoingInvoiceId}>
						<DataGrid.Label>{t("incoming_invoices.payments.offsets.less_offset")}</DataGrid.Label>
						<DataGrid.Value
							append={
								<span className="tabular-nums">
									{formatInvoiceAmount(offset.amount, payment.currency)}
								</span>
							}
						>
							<Link href={projectTabPath(offset.projectId, "invoices", hiddenTabs)}>
								{offset.number}
							</Link>
						</DataGrid.Value>
					</DataGrid.Row>
				))}
				<DataGrid.Row>
					<DataGrid.Label>
						{t(
							hasOffsets
								? "incoming_invoices.payments.offsets.transferred"
								: "incoming_invoices.payments.paid_amount",
						)}
					</DataGrid.Label>
					<DataGrid.Value>
						{hasOffsets && payment.amount === 0
							? t("incoming_invoices.payments.offsets.paid_by_offset")
							: formatInvoiceAmount(payment.amount, payment.currency)}
					</DataGrid.Value>
				</DataGrid.Row>
				<DataGrid.Row>
					<DataGrid.Label>{t("incoming_invoices.payments.skonto_taken")}</DataGrid.Label>
					<DataGrid.Value>
						{formatInvoiceAmount(payment.skontoAmount, payment.currency)}
					</DataGrid.Value>
				</DataGrid.Row>
				<DataGrid.Row>
					<DataGrid.Label>{t("incoming_invoices.payments.execution_date")}</DataGrid.Label>
					<DataGrid.Value>{formatInvoiceDate(payment.executionDate)}</DataGrid.Value>
				</DataGrid.Row>
			</DataGrid>
		</ContentCard>
	);
}
